/**
 * MicrosoftSignInButton — Vague 113 Wave 1k
 *
 * Login button "Continuer avec Microsoft" — analog to GoogleSignInButton.
 * Reads /api/auth/microsoft/status to know if Microsoft SSO is server-side
 * configured (env MICROSOFT_CLIENT_ID / MICROSOFT_CLIENT_SECRET). If not,
 * renders nothing.
 *
 * Click → calls /api/auth/microsoft/start which returns the Azure AD authorize
 * URL pre-built with the correct redirect_uri, state and minimal scopes.
 * Then we redirect the browser. Microsoft redirects back to
 * `{origin}/auth/microsoft` which is handled by `MicrosoftCallback.jsx`.
 *
 * REMINDER: DO NOT HARDCODE THE URL OR REDIRECT URI HERE — they come from the
 * backend `/api/auth/microsoft/start` response and Azure App Registration.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../api/client';
import { oauthRedirect } from '../utils/oauthRedirect';

export default function MicrosoftSignInButton({ onError }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // /auth/microsoft/status requires auth; for the login page we use a
    // public probe — the backend's `start` endpoint will refuse with 501 if
    // not configured. We just always render the button optimistically and
    // surface the 501 as an inline error.
    setStatus({ configured: true });
  }, []);

  const handleClick = async () => {
    setLoading(true);
    try {
      const returnOrigin = window.location.origin;
      const { data } = await api.get(`/auth/microsoft/start?return_origin=${encodeURIComponent(returnOrigin)}`);
      if (!data?.auth_url) {
        onError?.('Microsoft SSO non configuré côté serveur.');
        setLoading(false);
        return;
      }
      sessionStorage.setItem('ms_oauth_state', data.state || '');
      // Wave 1u3 — uses shared helper to handle the iframed-preview edge-case.
      oauthRedirect(data.auth_url, { onAfterPopup: () => setLoading(false) });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Démarrage Microsoft SSO impossible';
      onError?.(detail);
      setLoading(false);
    }
  };

  if (status === null) return null;

  return (
    <button
      type="button"
      data-testid="microsoft-signin-btn"
      onClick={handleClick}
      disabled={loading}
      className="w-full h-11 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] font-semibold text-[14px] flex items-center justify-center gap-3 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-[var(--ofm-primary)]" />
      ) : (
        // Microsoft logo (4 squares)
        <svg className="w-5 h-5" viewBox="0 0 23 23" aria-hidden="true">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#7FBA00" d="M12 1h10v10H12z" />
          <path fill="#00A4EF" d="M1 12h10v10H1z" />
          <path fill="#FFB900" d="M12 12h10v10H12z" />
        </svg>
      )}
      Continuer avec Microsoft
    </button>
  );
}
